import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, SlidersHorizontal, ArrowUpDown, Sparkles, MoreHorizontal,
  ChevronLeft, ChevronRight, Loader2, X, UserCheck, Clock,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Skeleton } from '../ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  PRIORITY_META, TONE_BADGE, statusMeta, fmtDate, QUEUE_TABS, STATUS_META,
} from './opsMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sélecteur d'opérateur enrichi (Responsable) avec recommandation.
const ResponsableSelect = ({ op, operators, disabled, onAssign }) => {
  const recommended = op.recommendedOperatorId
    ? operators.find((o) => o.operatorId === op.recommendedOperatorId)
    : null;
  return (
    <div className="min-w-[180px]">
      {!op.assignedOperatorId && recommended && (
        <p className="mb-0.5 flex items-center gap-1 text-[10px] font-medium text-emerald-600">
          <Sparkles className="h-3 w-3" /> Recommandé : {recommended.operatorName}
        </p>
      )}
      <Select
        value={op.assignedOperatorId || ''}
        disabled={disabled}
        onValueChange={(v) => onAssign(op, v)}
      >
        <SelectTrigger className="h-8 text-xs" data-testid={`ops-assign-select-${op.id}`}>
          <SelectValue placeholder="Assigner…" />
        </SelectTrigger>
        <SelectContent>
          {operators.map((o) => (
            <SelectItem key={o.operatorId} value={o.operatorId}>
              {o.operatorName} ({o.ongoing} actives)
              {o.operatorId === op.recommendedOperatorId ? ' · Recommandé' : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export const AssignmentQueue = ({ operators = [], status, onStatusChange, refreshKey, onMutated }) => {
  const [tab, setTab] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('priority');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [bulkOperator, setBulkOperator] = useState('');

  // Debounce recherche
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/ops/team/operations`, {
        params: { tab, status: status || '', search, sort, page, page_size: pageSize },
      });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossible de charger la file');
    } finally {
      setLoading(false);
    }
  }, [tab, status, search, sort, page, pageSize]);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { setSelected(new Set()); }, [tab, status, search, page, pageSize, refreshKey]);

  const ops = data?.operations || [];
  const counts = data?.tabCounts || {};
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const allOnPageSelected = ops.length > 0 && ops.every((o) => selected.has(o.id));

  const toggleAll = () => {
    setSelected((prev) => {
      if (allOnPageSelected) return new Set();
      return new Set(ops.map((o) => o.id));
    });
  };
  const toggleOne = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const assignOne = async (op, operatorId) => {
    if (!operatorId || operatorId === op.assignedOperatorId) return;
    setBusy(true);
    try {
      await axios.put(`${API}/transport-requests/${op.id}/operator`, { operatorId, reason: 'Répartition (File d\'assignation)' });
      toast.success(`${op.operationNumber} assignée`);
      await load();
      onMutated && onMutated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'assignation");
    } finally { setBusy(false); }
  };

  const bulkAssign = async () => {
    if (!bulkOperator || selected.size === 0) return;
    if (!window.confirm(`Assigner ${selected.size} opération(s) à cet opérateur ?`)) return;
    setBusy(true);
    const ids = Array.from(selected);
    let ok = 0;
    for (const id of ids) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await axios.put(`${API}/transport-requests/${id}/operator`, { operatorId: bulkOperator, reason: 'Assignation groupée Lead Ops' });
        ok += 1;
      } catch { /* continue */ }
    }
    toast.success(`${ok}/${ids.length} opération(s) assignée(s)`);
    setSelected(new Set());
    setBulkOperator('');
    await load();
    onMutated && onMutated();
    setBusy(false);
  };

  const statusLabel = status ? (STATUS_META[status]?.label || status) : null;

  const pageNumbers = useMemo(() => {
    const arr = [];
    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
    for (let i = start; i <= Math.min(totalPages, start + 4); i += 1) arr.push(i);
    return arr;
  }, [page, totalPages]);

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm" data-testid="ops-assignment-queue">
      {/* En-tête + recherche + filtres */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold tracking-tight text-gray-900">File d'assignation</h2>
          <span className="rounded-md bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700 ring-1 ring-amber-200">
            {counts.unassigned ?? 0}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Rechercher…"
              className="h-8 w-44 pl-8 text-xs"
              data-testid="ops-queue-search"
            />
          </div>
          <Button
            variant="outline" size="sm"
            className="h-8 px-2.5 text-xs"
            onClick={() => setSort((s) => (s === 'priority' ? 'date' : 'priority'))}
            data-testid="ops-queue-sort"
          >
            <ArrowUpDown className="mr-1 h-3.5 w-3.5" />
            Trier : {sort === 'priority' ? 'Priorité' : 'Date'}
          </Button>
          <Button variant="outline" size="sm" className="h-8 px-2.5 text-xs" disabled title="Filtres avancés (à venir)">
            <SlidersHorizontal className="mr-1 h-3.5 w-3.5" /> Filtres
          </Button>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex flex-wrap items-center gap-1 border-b border-gray-100 px-3 py-1.5">
        {QUEUE_TABS.map((t) => {
          const active = tab === t.key && !status;
          const c = counts[t.key];
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => { setTab(t.key); onStatusChange && onStatusChange(''); setPage(1); }}
              data-testid={`ops-queue-tab-${t.key}`}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                active ? 'bg-emerald-50 text-emerald-700' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
              }`}
            >
              {t.label}
              {c !== undefined && (
                <span className={`rounded px-1.5 text-[11px] tabular-nums ${
                  t.key === 'late' && c > 0 ? 'bg-red-100 text-red-600'
                    : t.key === 'unassigned' && c > 0 ? 'bg-amber-100 text-amber-600'
                      : 'bg-gray-100 text-gray-500'}`}>{c}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Chip filtre statut */}
      {status && (
        <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50/60 px-5 py-2">
          <span className="text-xs text-gray-500">Filtre statut :</span>
          <span className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-gray-200">
            {statusLabel}
            <button type="button" onClick={() => onStatusChange && onStatusChange('')} data-testid="ops-queue-clear-status">
              <X className="h-3 w-3 text-gray-400 hover:text-gray-600" />
            </button>
          </span>
        </div>
      )}

      {/* Barre d'actions groupées */}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 bg-emerald-50/40 px-5 py-2.5" data-testid="ops-bulk-bar">
          <span className="text-sm font-medium text-emerald-800">{selected.size} sélectionnée(s)</span>
          <div className="flex items-center gap-2">
            <Select value={bulkOperator} onValueChange={setBulkOperator}>
              <SelectTrigger className="h-8 w-56 text-xs" data-testid="ops-bulk-operator-select">
                <SelectValue placeholder="Assigner à un opérateur…" />
              </SelectTrigger>
              <SelectContent>
                {operators.map((o) => (
                  <SelectItem key={o.operatorId} value={o.operatorId}>{o.operatorName} ({o.ongoing} actives)</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" className="h-8 bg-emerald-600 text-xs hover:bg-emerald-700" disabled={!bulkOperator || busy} onClick={bulkAssign} data-testid="ops-bulk-assign-btn">
              {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <UserCheck className="mr-1 h-3.5 w-3.5" />}
              Assigner
            </Button>
          </div>
          <Button size="sm" variant="ghost" className="h-8 text-xs text-gray-500" onClick={() => setSelected(new Set())}>Annuler</Button>
        </div>
      )}

      {/* Tableau */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-gray-50/80 backdrop-blur">
            <tr className="text-[11px] uppercase tracking-wide text-gray-400">
              <th className="w-10 px-4 py-2.5"><Checkbox checked={allOnPageSelected} onCheckedChange={toggleAll} data-testid="ops-select-all" /></th>
              <th className="px-2 py-2.5 text-left font-medium">N° opération</th>
              <th className="px-2 py-2.5 text-left font-medium">Client</th>
              <th className="px-2 py-2.5 text-left font-medium">Trajet</th>
              <th className="px-2 py-2.5 text-left font-medium">Statut</th>
              <th className="px-2 py-2.5 text-left font-medium">Priorité</th>
              <th className="px-2 py-2.5 text-left font-medium">Date prévue</th>
              <th className="px-2 py-2.5 text-left font-medium">Responsable</th>
              <th className="px-2 py-2.5 text-left font-medium">Dernière activité</th>
              <th className="px-4 py-2.5 text-right font-medium">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading && Array.from({ length: 6 }).map((_, i) => (
              <tr key={`sk-${i}`}><td colSpan={10} className="px-4 py-2.5"><Skeleton className="h-6 w-full" /></td></tr>
            ))}

            {!loading && ops.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-16 text-center text-gray-400" data-testid="ops-queue-empty">
                Aucune opération dans cette vue 🎉
              </td></tr>
            )}

            {!loading && ops.map((op) => {
              const meta = statusMeta(op.status);
              const prio = PRIORITY_META[op.priority?.key] || PRIORITY_META.faible;
              return (
                <tr
                  key={op.id}
                  data-testid={`ops-queue-row-${op.id}`}
                  className={`transition-colors hover:bg-gray-50/60 ${op.isLate ? 'bg-red-50/40' : ''}`}
                >
                  <td className="px-4 py-2.5"><Checkbox checked={selected.has(op.id)} onCheckedChange={() => toggleOne(op.id)} data-testid={`ops-row-check-${op.id}`} /></td>
                  <td className="px-2 py-2.5 font-mono text-xs text-gray-700">{op.operationNumber}</td>
                  <td className="px-2 py-2.5 text-gray-700">{op.client}</td>
                  <td className="px-2 py-2.5 text-gray-500">{op.route}</td>
                  <td className="px-2 py-2.5">
                    <span className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ring-1 ${TONE_BADGE[meta.tone]}`}>{op.statusLabel}</span>
                  </td>
                  <td className="px-2 py-2.5">
                    <span className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ring-1 ${prio.cls}`}>{prio.label}</span>
                  </td>
                  <td className="px-2 py-2.5 text-xs text-gray-600">{fmtDate(op.deliveryDate)}</td>
                  <td className="px-2 py-2.5">
                    <ResponsableSelect op={op} operators={operators} disabled={busy} onAssign={assignOne} />
                  </td>
                  <td className="px-2 py-2.5">
                    <div className="flex items-center gap-1 text-xs text-gray-500"><Clock className="h-3 w-3 text-gray-300" />{op.lastActivityRelative}</div>
                    <div className="text-[11px] text-gray-400">{op.lastActivityLabel}</div>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`ops-row-menu-${op.id}`}>
                          <MoreHorizontal className="h-4 w-4 text-gray-400" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => window.open(`/admin/operations/${op.id}`, '_blank')}>
                          Ouvrir l'opération
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-5 py-3">
        <span className="text-xs text-gray-500">
          {total === 0 ? '0 opération' : `Affichage ${(page - 1) * pageSize + 1} à ${Math.min(page * pageSize, total)} sur ${total} opérations`}
        </span>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="ops-page-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          {pageNumbers.map((n) => (
            <Button
              key={n} size="sm"
              variant={n === page ? 'default' : 'outline'}
              className={`h-7 w-7 p-0 text-xs ${n === page ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
              onClick={() => setPage(n)}
              data-testid={`ops-page-${n}`}
            >{n}</Button>
          ))}
          <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="ops-page-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">Lignes par page</span>
          <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
            <SelectTrigger className="h-7 w-[68px] text-xs" data-testid="ops-page-size"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[10, 20, 50].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default AssignmentQueue;
